import { PRICE, priceRows } from "@/data/pricing";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Tag } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { ContextLinkBlock } from "@/components/ContextLinkBlock";
import { SALON_SERVICE_LINKS } from "@/data/localServiceLinks";
import { BarberGrid } from "@/components/BarberGrid";
import { SALON_BARBERARE, salonBarberLine } from "@/data/barbers";
import { Button } from "@/components/ui/button";
import { salonBySlug } from "@/data/salons";
import { ReviewsBlock } from "@/components/ReviewsBlock";
import { SalonRatingLines } from "@/components/SalonRatingLines";
import { SalonCtaButtons } from "@/components/SalonCtaButtons";
import AhlensWayfinder from "@/components/AhlensWayfinder";
import { offersForSalon } from "@/data/offers";
import { breadcrumbSchema, faqSchema, jsonLd, localBusinessSchema, SITE_URL, absUrl } from "@/lib/seo";
import { reviewsForSalon } from "@/data/reviews";
import ahlensUtsida1200 from "@/assets/opt/ahlens-utsida-1200.webp";
import ahlensSpeglar800 from "@/assets/opt/ahlens-speglar-800.webp";
import ahlensTvatt800 from "@/assets/opt/ahlens-tvatt-800.webp";
import { withBookingUtm } from "@/lib/booking";
const salon = salonBySlug["ahlens-city"];
const BOOK_URL = withBookingUtm("https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678");
const MAPS_LINK = "https://maps.google.com/?q=Tailor%27s+Barber+%C3%85hl%C3%A9ns+City+Stockholm";
const offers = offersForSalon(salon.slug);

const services = priceRows([
  "Herrklippning",
  "Skin Fade / Taper Fade",
  "Skäggtrimning",
  "Klippning & Skäggtrimning",
  "Barnklippning",
  "Seniorklippning 65+",
  "Slätrakning",
]);

const faq = [
  { q: "Var ligger Tailor's Barber Åhléns City?", a: "Vi ligger på plan 3 i Åhléns City på Klarabergsgatan 50, inne på herreavdelningen. Entrén är från Klarabergsgatan, 1 minut från T-Centralen." },
  { q: "Måste jag boka i förväg?", a: "Onlinebokning rekommenderas, men drop-in är välkommet i mån av tid. Tidig förmiddag och vardagar har vanligtvis kortast väntetid." },
  { q: "Hur fungerar drop-in hos er?", a: "Kom förbi salongen så hjälper vi dig så snart vi har en ledig barberare. Du får en uppskattad väntetid vid ankomst. Vill du garantera en tid rekommenderar vi att boka online via BokaDirekt." },
  { q: "Hur lång är väntetiden för drop-in?", a: "Väntetiden varierar beroende på dag och tid. Tidiga förmiddagar och vardagar är oftast lugnast, medan lördagseftermiddagar och söndagar kan vara mer uppbokade. Vid längre väntetid är du välkommen att shoppa i Åhléns under tiden." },
  { q: "Var kan jag parkera nära Åhléns City?", a: "Det enklaste är Åhléns Citys egen parkering i varuhusets garage. Eftersom vi ligger mitt i Stockholm City finns också flera allmänna parkeringshus i närheten, samt avgiftsbelagd gatuparkering längs Klarabergsgatan och Norra Smedjegatan." },
  { q: "Vilka betalningsmetoder tar ni emot?", a: "Vi tar emot kort och Swish." },
  { q: "Hur lång tid tar en herrklippning?", a: "En herrklippning tar normalt 30–40 minuter. Fade-klippningar kan ta 40–50 minuter. Kombinationer med skägg ca 60 minuter." },
  { q: "Klipper ni barn?", a: "Ja, barnklippning för barn upp till 10 år kostar 380 kr." },
  { q: "Är ni öppna på söndagar?", a: "Ja, vi har öppet söndag 11:00–19:00." },
  { q: "Säljer ni hårvårdsprodukter?", a: "Ja, vi säljer ett urval av professionella hårvårdsprodukter för herr i salongen." },
];

const localBiz = {
  ...localBusinessSchema(salon, reviewsForSalon(salon.slug)),
  description: "Barbersalong i Åhléns City, centrala Stockholm. 1 minut från T-Centralen. Herrklippning, taper fade, skin fade och skäggtrimning.",
  containedInPlace: {
    "@type": "ShoppingCenter",
    name: "Åhléns City",
    address: "Klarabergsgatan 50, Stockholm",
  },
  hasMap: MAPS_LINK,
  priceRange: "$$",
  telephone: "+468371156",
  parentOrganization: { "@type": "Organization", name: "Tailor's Barber", url: SITE_URL },
};

export const Route = createFileRoute("/barberare-stockholm-ahlens-city/")({
  head: () => ({
    meta: [
      { title: "Barber Stockholm City: 1 min från T-Centralen | Tailor's Barber" },
      { name: "description", content: "Barber Stockholm City på Åhléns, 1 min från T-Centralen. Herrklippning 450 kr, fade och skäggtrimning. Drop-in välkommet. Boka online på BokaDirekt." },
      { property: "og:title", content: "Barber Stockholm City: 1 min från T-Centralen | Tailor's Barber" },
      { property: "og:description", content: "Barber Stockholm City på Åhléns, 1 min från T-Centralen. Herrklippning 450 kr, fade och skäggtrimning." },
      { property: "og:url", content: absUrl(salon.url) },
      { property: "og:image", content: absUrl(salon.image) },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: absUrl(salon.url) },
      { rel: "preload", as: "image", href: ahlensUtsida1200, fetchpriority: "high" },
    ],

    scripts: [
      jsonLd(localBiz),
      jsonLd(faqSchema(faq)),
      jsonLd(breadcrumbSchema([
        { name: "Hem", url: "/" },
        { name: salon.shortName, url: salon.url },
      ])),
    ],
  }),
  component: AhlensCityPage,
});

function AhlensCityPage() {
  return (
    <SiteShell>
      <section className="container-page pt-6 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Åhléns City</span>
        </nav>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="eyebrow">Barber Stockholm City</div>
            <h1 className="mt-3 text-3xl md:text-5xl lg:text-6xl leading-[1.05]">
              Barber i Stockholm City – Tailor&apos;s Barber i Åhléns
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground/80 max-w-prose leading-relaxed">
              Tailor&apos;s Barber Åhléns City ligger i hjärtat av Stockholm – på plan 3 i Åhléns City,
              landets mest besökta varuhus. Vi ligger bokstavligen 60 sekunder från T-Centralen, vilket
              gör oss till den mest centralt belägna barbersalongen i Stockholm. Oavsett om du pendlar,
              är på besök i Stockholm eller jobbar i city – vi är alltid nära. Många hittar hit när de söker
               efter en frisör i Stockholm City och vill ha någon som jobbar med herrhår varje dag. Herrklippning {PRICE.herrklippning} kr, skäggtrimning {PRICE.skaggtrimning} kr, hår och skägg {PRICE.klippningSkagg} kr.
            </p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground/70">
              <SalonRatingLines salon="ahlens-city" />
              <span>📍 Klarabergsgatan 50, plan 3</span>
            </div>
            <div className="mt-5 flex flex-wrap gap-3 items-center">
              <Button asChild size="lg">
                <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka tid online</a>
              </Button>
              <span className="text-sm text-foreground/70">Drop-in välkommen i mån av tid</span>
            </div>
          </div>
          <div className="grid grid-cols-3 grid-rows-2 gap-3 aspect-[4/3]">
            <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden bg-muted shadow-soft">
              <img src={ahlensUtsida1200} alt="Barber Stockholm City, Tailor's Barber Åhléns, 1 minut från T-Centralen" width={1200} height={1601} fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
            </div>
            <div className="rounded-xl overflow-hidden bg-muted shadow-soft">
              <img src={ahlensSpeglar800} alt="Barberarstationer med speglar hos Tailor's Barber Åhléns City" width={800} height={600} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>
            <div className="rounded-xl overflow-hidden bg-muted shadow-soft">
              <img src={ahlensTvatt800} alt="Hårtvättstation hos Tailor's Barber Åhléns City" width={800} height={1070} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>

          </div>
        </div>
      </section>

      <section className="container-page min-w-0 overflow-hidden py-10 [&_.wf>div:last-child>div:first-child]:min-w-0">
        <h2 className="mb-5 text-2xl md:text-3xl">Hitta hit</h2>
        <AhlensWayfinder lang="sv" />
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="container-page py-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="font-medium">Öppettider Åhléns City:</span>
          <span><strong>Mån–Fre</strong> 10:00–20:00</span>
          <span><strong>Lör</strong> 10:00–19:00</span>
          <span><strong>Sön</strong> 11:00–19:00</span>
        </div>
      </section>




      {/* Offers */}
      <section className="container-page pb-6 md:pb-10">
        {offers.length > 0 ? (
          offers.map((o) => (
            <div
              key={o.id}
              className="card-elev p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5 border-brass-deep/30"
            >
              <Tag className="text-brass-deep shrink-0" size={28} aria-hidden />
              <div className="flex-1">
                <div className="eyebrow">Aktuellt erbjudande</div>
                <h2 className="mt-1 text-xl md:text-2xl">{o.title}</h2>
                <p className="text-sm text-muted-foreground mt-1">{o.description}</p>
              </div>
              <SalonCtaButtons
                salon={salon}
                kinds={["book"]}
                ctaLocation="salon_offer"
                primary="brass"
              />
            </div>
          ))
        ) : (
          <div className="card-elev p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5">
            <Tag className="text-brass-deep shrink-0" size={28} aria-hidden />
            <div className="flex-1 min-w-0">
              <div className="eyebrow">Erbjudanden</div>
              <p className="mt-1 text-sm text-foreground/80">
                Just nu finns inga aktiva erbjudanden för {salon.shortName}. Boka din tid online eller se aktuella priser på BokaDirekt.
              </p>
            </div>
            <SalonCtaButtons salon={salon} kinds={["book"]} ctaLocation="empty_offers" primary="brass" />
          </div>
        )}
      </section>

      <section className="container-page pt-16 md:pt-20 pb-10">
        <h2 className="text-2xl md:text-3xl">Barber Stockholm City mitt i Åhléns</h2>
        <p className="mt-4 text-foreground/80 max-w-3xl">
          Barber Stockholm City på Åhléns är en av fyra Tailor&apos;s Barber-salonger. Den centrala
          placeringen på plan 3 inne i Åhléns innebär:
        </p>
        <ul className="mt-5 grid gap-3 md:grid-cols-2 max-w-3xl">
          <li className="card-elev p-5"><strong>1 minut från T-Centralen</strong>, perfekt för dig som pendlar</li>
          <li className="card-elev p-5"><strong>Nära Sergels Torg, Drottninggatan och Kulturhuset</strong>, mitt i stadens puls</li>
          <li className="card-elev p-5"><strong>Enkelt att kombinera</strong> med shopping, arbete eller ett besök i city</li>
          <li className="card-elev p-5"><strong>Öppet alla dagar i veckan</strong>, även kvällar och helger</li>
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl mb-5">Våra tjänster i Åhléns City</h2>
        <div className="card-elev overflow-hidden max-w-3xl">
          <table className="w-full text-left">
            <tbody>
              {services.map(([name, price], i) => (
                <tr key={i} className={i > 0 ? "border-t border-border/60" : ""}>
                  <td className="px-5 py-4">{name}</td>
                  <td className="px-5 py-4 text-right font-medium whitespace-nowrap">{price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-foreground/70 max-w-3xl">
          Alla behandlingar inkluderar konsultation och styling. Se hela vår{" "}
          <Link to="/priser/" className="underline">prislista</Link>.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Öppettider – Åhléns City</h2>
        <ul className="mt-4 space-y-1 text-base md:text-lg text-foreground/85">
          <li><strong>Måndag–Fredag:</strong> 10:00–20:00</li>
          <li><strong>Lördag:</strong> 10:00–19:00</li>
          <li><strong>Söndag:</strong> 11:00–19:00</li>
        </ul>
        <p className="mt-3 text-sm text-foreground/70">
          Vi följer Åhléns öppettider och kan ha avvikande tider under helgdagar.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Varför välja Tailor&apos;s Barber i Åhléns City?</h2>
        <p className="mt-4 text-foreground/80 max-w-3xl">
          Vi är inte en walk-up klippare i ett köpcentrum. Vi är en dedikerad barbersalong med utbildade
          barberare som specialiserat sig på herrklippning, fade-tekniker och skäggvård. Varje klippning
          börjar med en konsultation – vi hittar rätt stil för just dig.
        </p>
        <h3 className="mt-6 text-lg font-medium">Vad vi är kända för</h3>
        <ul className="mt-3 space-y-2 text-foreground/85 max-w-3xl list-disc list-inside">
          <li>Precision i fadetekniker – <Link to="/taper-fade/" className="underline">taper fade, skin fade</Link>, drop fade</li>
          <li>Klassisk <Link to="/herrklippning/" className="underline">herrklippning</Link> med modern känsla</li>
          <li><Link to="/skaggtrimning/" className="underline">Skäggforming och skäggtrimning</Link></li>
          <li>Varmt och professionellt bemötande</li>
          <li>Alltid konsekvent resultat – du kan alltid boka samma barberare</li>
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Boka barber Stockholm City online</h2>
        <ul className="mt-4 space-y-2 text-foreground/85 max-w-3xl">
          <li>Onlinebokning via BokaDirekt, välj tid och barberare som passar dig</li>
          <li>Drop-in välkommet i mån av tid, tidig förmiddag rekommenderas för kortast väntetid. Så fungerar <Link to="/drop-in-barber-stockholm/" className="underline">drop-in hos barberare i Stockholm</Link>.</li>
        </ul>
        <div className="mt-6">
          <Button asChild size="lg">
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka online</a>
          </Button>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Barber eller frisör i Stockholm City?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Skillnaden är i praktiken vad man arbetar med varje dag. En frisör utbildas för både dam- och
          herrhår, färgning och längre frisyrer. En barber eller barberare arbetar nästan uteslutande med
          herrhår: korta klippningar, fades, linjering och skägg, och använder maskin och rakkniv i en helt
          annan utsträckning. När du söker efter en barber Stockholm City får du alltså en annan expertis.
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          För en herrklippning med tydlig övergång, ren nacklinje eller ett skägg som ska formas efter
          ansiktet är barbershopen därför oftast rätt val. Vi klipper ett par tusen herrar om året på
          Åhléns City, vilket gör att vi snabbt ser vad som fungerar med just din hårtyp och hårväxt. Vill
          du färga håret eller ha en längre frisyr är en frisörsalong bättre lämpad, då säger vi det
          hellre än att boka in dig.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Fler salonger i Stockholmsregionen</h2>
        <p className="mt-4 text-foreground/80 max-w-3xl">
          Om du befinner dig utanför city har vi också salonger i:
        </p>
        <ul className="mt-4 space-y-2 text-foreground/85 max-w-3xl list-disc list-inside">
          <li><Link to="/barberare-stockholm-taby/" className="underline">Täby Centrum</Link> – Biblioteksgången 1, Täby</li>
          <li><Link to="/barberare-stockholm-akersberga/" className="underline">Åkersberga</Link> – ICA Maxi Åkersberga</li>
          <li><Link to="/barberare-stockholm-arninge/" className="underline">Arninge</Link> – ICA Maxi Arninge, Täby</li>
          <li><Link to="/" className="underline">Alla salonger i Stockholm</Link></li>
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl mb-6">Teamet på Åhléns City</h2>
        <p className="mt-0 mb-6 text-foreground/80">{salonBarberLine("ahlens-city")}</p>
        <BarberGrid barbers={SALON_BARBERARE["ahlens-city"].barbers} salonName="Åhléns City" />
      </section>

      <ReviewsBlock salon="ahlens-city" heading="Vad våra kunder säger om Åhléns City" />

      <ContextLinkBlock
        heading="Tjänster i Åhléns City"
        links={SALON_SERVICE_LINKS["ahlens-city"]!.links}
      />

      <Faq items={faq} heading="Vanliga frågor" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Redo att boka?</h2>
          <p className="mt-3 text-foreground/80">Central barber, 1 minut från T-Centralen.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka tid online</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
